import { DonationStatus } from '@spi/shared';
import type { Donation } from '@spi/shared';

const steps = [
  { status: DonationStatus.PLEDGED, label: 'Pledged', dateKey: 'createdAt' as const },
  { status: DonationStatus.SHIPPED, label: 'Shipped', dateKey: 'shippedAt' as const },
  { status: DonationStatus.DELIVERED, label: 'Delivered', dateKey: 'deliveredAt' as const },
  { status: DonationStatus.RECEIVED, label: 'Received', dateKey: 'receivedAt' as const },
  { status: DonationStatus.VERIFIED, label: 'Verified', dateKey: 'verifiedAt' as const },
];

const statusOrder: Record<string, number> = {
  [DonationStatus.PLEDGED]: 0,
  [DonationStatus.SHIPPED]: 1,
  [DonationStatus.DELIVERED]: 2,
  [DonationStatus.RECEIVED]: 3,
  [DonationStatus.VERIFIED]: 4,
  [DonationStatus.CANCELLED]: -1,
};

export function DonationTimeline({ donation }: { donation: Donation }) {
  const currentIdx = statusOrder[donation.status] ?? -1;

  if (donation.status === DonationStatus.CANCELLED) {
    return (
      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <span className="text-sm font-medium text-gray-600">Donation Cancelled</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {steps.map((step, i) => {
        const isCompleted = i <= currentIdx;
        const isCurrent = i === currentIdx;
        const date = donation[step.dateKey];

        return (
          <div key={step.status} className="flex items-center gap-2 flex-1">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                isCompleted ? 'bg-spi-green text-white' : 'bg-spi-parchment text-spi-slate'
              } ${isCurrent ? 'ring-2 ring-spi-green ring-offset-2' : ''}`}>
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span className="text-xs mt-1 font-medium text-spi-charcoal">{step.label}</span>
              {date && <span className="text-[10px] text-spi-slate">{new Date(date).toLocaleDateString()}</span>}
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 ${i < currentIdx ? 'bg-spi-green' : 'bg-spi-parchment'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}
